import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Radio } from 'tea-component';
import { getLocale } from 'umi';
import { IRadioSwitch, IRadioOption } from './type';
import './index.scss';

const EMPTY_OPTIONS: IRadioOption[] = [];

interface IRadioSwitchChildProps {
  config?: Record<string, unknown>;
}

/**
 * 公共 Radio 切换组件（参照 SegmentCo 模式）
 *
 * 功能：
 * 1. 顶部渲染 tea Radio.Group
 * 2. 通过 changeData(dataKey, val) 将选中值写入上层 data
 * 3. 支持 childrenRenderMode: 'byDataKey' 按值过滤 children（只渲染匹配的子节点）
 * 4. 不配 childrenRenderMode 时所有 children 都渲染（下游通过 data[dataKey] 自行判断）
 *
 * pageConfig 用法示例：
 * {
 *   "name": "RadioSwitch",
 *   "data": { "alignment": "last_version" },
 *   "props": {
 *     "config": {
 *       "dataKey": "alignment",
 *       "options": [
 *         { "value": "last_version", "name": "Version Aligned", "nameCn": "版本周对齐" },
 *         { "value": "last_day", "name": "Natural Day Aligned", "nameCn": "自然日对齐" }
 *       ]
 *     }
 *   },
 *   "children": [...]
 * }
 */
const RadioSwitch = (props: IRadioSwitch) => {
  const isEN = getLocale() === 'en-US';
  const {
    config,
    data,
    changeData,
    children,
    className,
    style,
    childrenRenderMode,
    childrenMatchField,
  } = props;

  const {
    options: configOptions = EMPTY_OPTIONS,
    dataKey = '',
  } = config || {};

  const defaultValue = useMemo(() => {
    const savedVal = dataKey && data?.[dataKey];
    if (
      typeof savedVal === 'string' &&
      configOptions?.some((opt) => opt.value === savedVal)
    ) {
      return savedVal;
    }
    return configOptions?.[0]?.value || '';
  }, [data, dataKey, configOptions]);

  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
    if (dataKey && defaultValue && data?.[dataKey] !== defaultValue) {
      changeData?.(dataKey, defaultValue);
    }
  }, [data, dataKey, defaultValue]);

  const options = useMemo(
    () =>
      configOptions?.map((opt: IRadioOption) => ({
        label: isEN ? opt.name : opt.nameCn,
        value: opt.value,
      })) || [],
    [configOptions, isEN],
  );

  const filteredChildren = useMemo(() => {
    if (childrenRenderMode !== 'byDataKey') {
      return React.Children.toArray(children);
    }
    const matchField = childrenMatchField || 'id';
    return React.Children.toArray(children).filter((child) => {
      if (!React.isValidElement<IRadioSwitchChildProps>(child)) {
        return true;
      }
      const childId = child?.props?.config?.[matchField];
      return typeof childId !== 'string' || childId === value;
    });
  }, [children, childrenRenderMode, childrenMatchField, value]);

  return (
    <div
      className={classNames('dbd-radio-switch', className)}
      style={style}
    >
      <Radio.Group
        className="dbd-radio-switch__radio"
        value={value}
        onChange={(val: string) => {
          setValue(val);
          if (dataKey && changeData) {
            changeData(dataKey, val);
          }
        }}
      >
        {options.map((opt) => (
          <Radio key={opt.value} name={opt.value}>
            {opt.label}
          </Radio>
        ))}
      </Radio.Group>
      <div className="dbd-radio-switch__content">
        {filteredChildren}
      </div>
    </div>
  );
};

export default RadioSwitch;
